import {
    MouseEvent,
    SyntheticEvent,
    HTMLAttributes,
    CSSProperties,
    useState
} from 'react';
import { imageStyle, defaultStyle } from './image.css';

const placeHolderImg =
    "data:image/svg+xml;charset=utf-8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 3 2'></svg>";

const generateSrcSet = (img: Img[]) => {
    let res = '';

    img.forEach((item, idx) => {
        // add srcset per width
        res += `${item.src} ${item.width}w`;

        // add comma at the end if not the last item
        if (idx < img.length - 1) {
            res += ', ';
        }
    });

    return res;
};

type Sources = { type: 'image/webp' | 'image/jpeg'; srcSet: string }[];

export type StaticGoogleMapImgStyles = Pick<
    CSSProperties,
    | 'objectFit'
    | 'objectPosition'
    | 'height'
    | 'width'
    | 'borderRadius'
    | 'display'
>;

type Img = {
    width: number;
    src: string;
};

export type ImageProps = {
    src: string;
    alt: string;
    lazy?: boolean;
    important?: boolean;
    width?: number;
    height?: number;
    onClick?: (e: MouseEvent<HTMLImageElement>) => void;
    title?: string;
    onLoad?: (e: SyntheticEvent<HTMLImageElement, Event>) => void;
    onError?: (e: SyntheticEvent<HTMLImageElement, Event>) => void;
    decorative?: boolean;
    style?: StaticGoogleMapImgStyles | string;
    srcSet?: string;
    sizes?: string;
    responsiveImgList?: Img[];
    sources?: Sources;
    objectFit?: 'cover' | 'contain';
} & HTMLAttributes<HTMLImageElement>;

export function Image({
    decorative,
    lazy,
    important,
    responsiveImgList,
    srcSet,
    onError,
    className,
    sources,
    objectFit,
    ...props
}: ImageProps) {
    const loading = lazy ? 'lazy' : important ? 'eager' : undefined;
    const [showPlaceholder, setShowPlaceholder] = useState(false);

    function handleError() {
        setShowPlaceholder(true);
    }

    if (showPlaceholder) {
        return (
            <img
                {...props}
                className={`${defaultStyle} ${className ?? ''}`}
                alt={decorative ? '' : props.alt}
                src={placeHolderImg}
            />
        );
    }

    if (sources) {
        // note that we purposefully omit img.src to stop safari & firefox double fetching
        const { src, sizes, alt, ...safeProps } = props;
        return (
            <picture>
                {sources?.map((source) => (
                    <source
                        srcSet={source.srcSet}
                        type={
                            source.type === 'image/jpeg'
                                ? undefined
                                : source.type
                        }
                        sizes={props.sizes}
                        key={source.type}
                    />
                ))}
                <img
                    {...safeProps}
                    className={`${imageStyle({ objectFit })} ${
                        className ?? ''
                    }`}
                    onError={handleError}
                    loading={loading}
                    alt={decorative ? '' : props.alt}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    fetchPriority={important ? 'high' : 'auto'}
                />
            </picture>
        );
    }

    return (
        <img
            {...props}
            className={`${defaultStyle} ${className ?? ''}`}
            onError={handleError}
            loading={loading}
            alt={decorative ? '' : props.alt}
            srcSet={
                responsiveImgList ? generateSrcSet(responsiveImgList) : srcSet
            }
        />
    );
}

export default Image;
